/**
 * Synthesized SFX system using Web Audio API.
 *
 * Audio bus topology:
 *                      masterGain
 *               /     |      |      \
 *          ambient  music   sfx     ui
 *
 * Each bus is a GainNode connected to master. Bus volumes are independently
 * controllable. Combat/movement SFX route to sfx bus, UI sounds to ui bus,
 * stingers (turn start/end, victory, defeat) to music bus. Ambient bus is
 * prepared for biome stems (Phase 2).
 *
 * Heavy SFX (explosions, impacts) duck the music bus momentarily.
 * Volume persisted to localStorage. Lazy AudioContext creation for autoplay policy.
 */

// ---------------------------------------------------------------------------
// Bus types & storage
// ---------------------------------------------------------------------------

export type AudioBus = 'master' | 'ambient' | 'music' | 'sfx' | 'ui';

const BUS_STORAGE_KEYS: Record<AudioBus, string> = {
  master: 'orbituary-sfx-volume',  // reuses legacy key for seamless migration
  ambient: 'orbituary-vol-ambient',
  music: 'orbituary-vol-music',
  sfx: 'orbituary-vol-sfx',
  ui: 'orbituary-vol-ui',
};

const BUS_DEFAULTS: Record<AudioBus, number> = {
  master: 0.6,
  ambient: 0.5,
  music: 0.4,
  sfx: 0.8,
  ui: 0.7,
};

// ---------------------------------------------------------------------------
// Module-private state
// ---------------------------------------------------------------------------

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let ambientBus: GainNode | null = null;
let musicBus: GainNode | null = null;
let sfxBus: GainNode | null = null;
let uiBus: GainNode | null = null;
// 67.22 (owner, Oct 10: "music is too loud"): the music bus runs through a duck (dips under gunfire) and a fixed trim
// before the master — after the player's slider, so the slider and its saved value are untouched
let musicDuck: GainNode | null = null;
/** 67.26: the ambient bus's duck stage (after its volume): the weather dips under gunfire with the music, by AMBIENT_DUCK_SHARE of the music's dip. */
let ambientDuck: GainNode | null = null;
export const AMBIENT_DUCK_SHARE = 0.6;
let musicTrim: GainNode | null = null;
/** The music's fixed trim against the effects: −6 dB. */
export const MUSIC_TRIM = 0.5;

// Active bus for routing — set by SFX functions before calling helpers.
// Default (null) routes to sfxBus. Synchronous-only, safe in single-threaded JS.
let _activeBus: GainNode | null = null;

// Spatial panner — when set, gain() routes through this PannerNode instead of
// directly to the bus. The panner itself is pre-connected to the correct bus.
let _spatialPanner: PannerNode | null = null;

// Stinger priority — a dedicated gain node for stingers so we can cancel the
// previous stinger by fading this node to 0 before playing a new one.
let stingerGain: GainNode | null = null;

// ---------------------------------------------------------------------------
// Stored volume helpers
// ---------------------------------------------------------------------------

function getStoredVolume(bus: AudioBus): number {
  const stored = localStorage.getItem(BUS_STORAGE_KEYS[bus]);
  return stored !== null ? parseFloat(stored) : BUS_DEFAULTS[bus];
}

// ---------------------------------------------------------------------------
// Lazy context creation
// ---------------------------------------------------------------------------

function ensureContext(): boolean {
  if (ctx) {
    if (ctx.state === 'suspended') ctx.resume();
    return true;
  }
  try {
    ctx = new AudioContext();

    masterGain = ctx.createGain();
    masterGain.gain.value = getStoredVolume('master');
    masterGain.connect(ctx.destination);

    ambientBus = ctx.createGain();
    ambientBus.gain.value = getStoredVolume('ambient');
    ambientDuck = ctx.createGain();
    ambientBus.connect(ambientDuck);
    ambientDuck.connect(masterGain);

    musicBus = ctx.createGain();
    musicBus.gain.value = getStoredVolume('music');
    musicDuck = ctx.createGain();
    musicTrim = ctx.createGain();
    musicTrim.gain.value = MUSIC_TRIM;
    musicBus.connect(musicDuck);
    musicDuck.connect(musicTrim);
    musicTrim.connect(masterGain);

    sfxBus = ctx.createGain();
    sfxBus.gain.value = getStoredVolume('sfx');
    sfxBus.connect(masterGain);

    uiBus = ctx.createGain();
    uiBus.gain.value = getStoredVolume('ui');
    uiBus.connect(masterGain);

    stingerGain = ctx.createGain();
    stingerGain.gain.value = 1.0;
    stingerGain.connect(musicBus);

    if (ctx.state === 'suspended') ctx.resume();
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Helpers (ported from reference lines 227–293)
// ---------------------------------------------------------------------------

interface NoiseBurstOpts {
  duration?: number;
  attack?: number;
  decay?: number;
  freq?: number;
  Q?: number;
  type?: BiquadFilterType;
  vol?: number;
  delay?: number;
}

interface OscOpts {
  freq?: number;
  type?: OscillatorType;
  duration?: number;
  attack?: number;
  decay?: number;
  vol?: number;
  delay?: number;
  freqEnd?: number | null;
  detune?: number;
}

function now(): number {
  // Small offset so sounds scheduled immediately after resume() aren't in the past
  return ctx!.currentTime + 0.02;
}

function gain(vol: number): GainNode {
  const g = ctx!.createGain();
  g.gain.value = vol;
  g.connect(_spatialPanner ?? _activeBus ?? sfxBus!);
  return g;
}

function noiseBuffer(duration: number): AudioBuffer {
  const len = ctx!.sampleRate * duration;
  const buf = ctx!.createBuffer(1, len, ctx!.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

function noiseBurst(opts: NoiseBurstOpts): { src: AudioBufferSourceNode; gain: GainNode; filter: BiquadFilterNode } {
  const { duration = 0.3, attack = 0.01, freq = 1000, Q = 1,
          type = 'lowpass', vol = 0.5, delay = 0 } = opts;
  const t = now() + delay;
  const g = gain(0);
  const filter = ctx!.createBiquadFilter();
  filter.type = type;
  filter.frequency.value = freq;
  filter.Q.value = Q;
  filter.connect(g);

  const src = ctx!.createBufferSource();
  src.buffer = noiseBuffer(duration + 0.1);
  src.connect(filter);

  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + attack);
  g.gain.exponentialRampToValueAtTime(0.001, t + duration);

  src.start(t);
  src.stop(t + duration + 0.1);
  return { src, gain: g, filter };
}

function osc(opts: OscOpts): { osc: OscillatorNode; gain: GainNode } {
  const { freq = 440, type = 'sine', duration = 0.3, attack = 0.01,
          vol = 0.3, delay = 0, freqEnd = null, detune = 0 } = opts;
  const t = now() + delay;
  const g = gain(0);
  const o = ctx!.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  if (freqEnd !== null && freqEnd !== undefined) {
    o.frequency.exponentialRampToValueAtTime(Math.max(freqEnd, 1), t + duration);
  }
  o.detune.value = detune;
  o.connect(g);

  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(vol, t + attack);
  g.gain.exponentialRampToValueAtTime(0.001, t + duration);

  o.start(t);
  o.stop(t + duration + 0.1);
  return { osc: o, gain: g };
}

// ---------------------------------------------------------------------------
// Ducking — temporarily lower music bus on heavy SFX
// ---------------------------------------------------------------------------

function duckMusicBus(): void {
  if (!musicBus || !ctx) return;
  const t = ctx.currentTime;
  const target = getStoredVolume('music');
  musicBus.gain.cancelScheduledValues(t);
  // Duck to 50% immediately, hold for 0.3s, ramp back over 0.5s
  musicBus.gain.setValueAtTime(target * 0.5, t);
  musicBus.gain.setValueAtTime(target * 0.5, t + 0.3);
  musicBus.gain.linearRampToValueAtTime(target, t + 0.8);
}

// ---------------------------------------------------------------------------
// Public API — Volume
// ---------------------------------------------------------------------------

/**
 * 67.22: dip the music to `gain` (0–1; 1 = no dip) for a moment — a shot or an impact heard loud — and let it come
 * back over about a second. A deeper dip wins over a shallower one already under way.
 */
/** 67.27 (b): the music's duck stage, for sounds that sit under the music and dip under gunfire with it (a drone's hum). */
export function getDuckedBus(): GainNode | null { return musicDuck; }

export function duckMusic(gain: number): void {
  if (!ctx || !musicDuck || gain >= 0.999) return;
  const t = ctx.currentTime, g = musicDuck.gain;
  const target = Math.min(g.value, Math.max(0, gain));
  g.cancelScheduledValues(t);
  g.setValueAtTime(g.value, t);
  g.setTargetAtTime(target, t, 0.02);          // in fast
  g.setTargetAtTime(1, t + 0.18, 0.35);        // back over about a second
  // 67.26: the weather (the ambient bus) dips with it, less deep
  if (ambientDuck) {
    const a = ambientDuck.gain, aTarget = Math.min(a.value, 1 - (1 - Math.max(0, gain)) * AMBIENT_DUCK_SHARE);
    a.cancelScheduledValues(t); a.setValueAtTime(a.value, t);
    a.setTargetAtTime(aTarget, t, 0.02); a.setTargetAtTime(1, t + 0.18, 0.35);
  }
}

export function initAudio(): void {
  ensureContext();
}

function getBusNode(bus: AudioBus): GainNode | null {
  switch (bus) {
    case 'master': return masterGain;
    case 'ambient': return ambientBus;
    case 'music': return musicBus;
    case 'sfx': return sfxBus;
    case 'ui': return uiBus;
  }
}

export function setBusVolume(bus: AudioBus, v: number): void {
  const clamped = Math.max(0, Math.min(1, v));
  localStorage.setItem(BUS_STORAGE_KEYS[bus], String(clamped));
  const node = getBusNode(bus);
  if (node) node.gain.value = clamped;
}

export function getBusVolume(bus: AudioBus): number {
  const node = getBusNode(bus);
  if (node) return node.gain.value;
  return getStoredVolume(bus);
}

export function setSfxVolume(v: number): void {
  setBusVolume('master', v);
}

export function getSfxVolume(): number {
  return getBusVolume('master');
}

export function getAudioContext(): AudioContext | null {
  return ctx;
}

export function getAmbientBus(): GainNode | null {
  return ambientBus;
}

export function getMusicBus(): GainNode | null {
  return musicBus;
}

export function getSfxBus(): GainNode | null {
  return sfxBus;
}

// ---------------------------------------------------------------------------
// Spatial Audio
// ---------------------------------------------------------------------------

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

/**
 * Play an SFX function with 3D spatial positioning.
 * Creates a PannerNode at the given world position, routes all sounds
 * created by `fn` through it, then auto-disconnects after `lifetime` seconds.
 */
export function playSpatial(pos: Vec3Like, fn: () => void, lifetime = 3): void {
  if (!ctx) return;
  const panner = ctx.createPanner();
  panner.panningModel = 'equalpower';
  panner.distanceModel = 'inverse';
  panner.refDistance = 50;
  panner.maxDistance = 200;
  panner.rolloffFactor = 0.3;
  panner.positionX.value = pos.x;
  panner.positionY.value = pos.y;
  panner.positionZ.value = pos.z;
  panner.connect(_activeBus ?? sfxBus!);

  _spatialPanner = panner;
  fn();
  _spatialPanner = null;

  // Auto-disconnect after sounds finish
  setTimeout(() => { try { panner.disconnect(); } catch { /* already disconnected */ } }, lifetime * 1000);
}

/**
 * Update the AudioListener position to match the camera.
 * Call once per frame from the render loop.
 */
export function updateListenerPosition(
  pos: Vec3Like,
  forward: Vec3Like,
  up: Vec3Like,
): void {
  if (!ctx) return;
  const l = ctx.listener;
  if (l.positionX !== undefined) {
    // Modern API (AudioParam-based)
    l.positionX.value = pos.x;
    l.positionY.value = pos.y;
    l.positionZ.value = pos.z;
    l.forwardX.value = forward.x;
    l.forwardY.value = forward.y;
    l.forwardZ.value = forward.z;
    l.upX.value = up.x;
    l.upY.value = up.y;
    l.upZ.value = up.z;
  } else {
    // Legacy fallback
    l.setPosition(pos.x, pos.y, pos.z);
    l.setOrientation(forward.x, forward.y, forward.z, up.x, up.y, up.z);
  }
}

// ---------------------------------------------------------------------------
// SFX — Combat (ported from reference lines 302–423)
// ---------------------------------------------------------------------------

export function rocketLaunch(): void {
  if (!ensureContext()) return;
  duckMusicBus();
  // Soft ignition thump
  osc({ freq: 90, freqEnd: 35, type: 'sine', duration: 0.195, vol: 0.2, attack: 0.007 });
  // Initial burst — broadband crack of ignition
  noiseBurst({ duration: 0.08, attack: 0.001, freq: 2500, Q: 0.5, vol: 0.25 });
  // Main rush — wide noise with aggressive filter sweep
  const n1 = noiseBurst({ duration: 1.04, attack: 0.013, freq: 1500, Q: 0.7, vol: 0.35 });
  n1.filter.frequency.setValueAtTime(600, now());
  n1.filter.frequency.exponentialRampToValueAtTime(4000, now() + 0.195);
  n1.filter.frequency.exponentialRampToValueAtTime(1200, now() + 1.04);
  // High-freq tearing layer
  const n2 = noiseBurst({ duration: 0.65, attack: 0.007, freq: 6000, Q: 1.5, vol: 0.12, type: 'bandpass' });
  n2.filter.frequency.setValueAtTime(3000, now());
  n2.filter.frequency.exponentialRampToValueAtTime(8000, now() + 0.13);
  n2.filter.frequency.exponentialRampToValueAtTime(2000, now() + 0.65);
  // Doppler-ish receding whoosh
  const n3 = noiseBurst({ duration: 0.78, attack: 0.065, freq: 1000, Q: 1, vol: 0.15, delay: 0.195 });
  n3.filter.frequency.setValueAtTime(2000, now() + 0.195);
  n3.filter.frequency.exponentialRampToValueAtTime(300, now() + 0.975);
  // Hiss tail
  noiseBurst({ duration: 0.78, attack: 0.13, freq: 5000, Q: 0.3, vol: 0.07, delay: 0.26, type: 'highpass' });
}

export function explosionSmall(): void {
  if (!ensureContext()) return;
  duckMusicBus();
  // Initial crack
  noiseBurst({ duration: 0.05, attack: 0.001, freq: 3500, Q: 0.5, vol: 0.4 });
  noiseBurst({ duration: 0.03, attack: 0.001, freq: 1200, Q: 0.8, vol: 0.3 });
  // Low body
  const body = noiseBurst({ duration: 0.6, attack: 0.003, freq: 150, Q: 0.7, vol: 0.5 });
  body.filter.frequency.setValueAtTime(200, now());
  body.filter.frequency.exponentialRampToValueAtTime(60, now() + 0.4);
  // Sub anchor
  osc({ freq: 65, freqEnd: 25, type: 'sine', duration: 0.3, vol: 0.12, attack: 0.01 });
  // Mid-range chaos
  const mid = noiseBurst({ duration: 0.35, attack: 0.005, freq: 800, Q: 0.5, vol: 0.3 });
  mid.filter.frequency.setValueAtTime(1500, now());
  mid.filter.frequency.exponentialRampToValueAtTime(400, now() + 0.35);
  // Debris scatter
  for (let i = 0; i < 4; i++) {
    const d = 0.04 + Math.random() * 0.2;
    noiseBurst({ duration: 0.04 + Math.random() * 0.04, attack: 0.001,
                 freq: 800 + Math.random() * 2000, Q: 1.5, vol: 0.06 + Math.random() * 0.06,
                 delay: d, type: 'bandpass' });
  }
  // Settling rumble
  noiseBurst({ duration: 0.5, attack: 0.06, freq: 350, Q: 0.5, vol: 0.08, delay: 0.12 });
}

export function explosionLarge(): void {
  if (!ensureContext()) return;
  duckMusicBus();
  // Layered initial blast
  noiseBurst({ duration: 0.07, attack: 0.001, freq: 5000, Q: 0.3, vol: 0.45 });
  noiseBurst({ duration: 0.05, attack: 0.001, freq: 1500, Q: 0.6, vol: 0.4 });
  noiseBurst({ duration: 0.04, attack: 0.001, freq: 600, Q: 1, vol: 0.35 });
  // Low body
  const body1 = noiseBurst({ duration: 1.0, attack: 0.005, freq: 120, Q: 0.8, vol: 0.55 });
  body1.filter.frequency.setValueAtTime(250, now());
  body1.filter.frequency.exponentialRampToValueAtTime(40, now() + 0.8);
  // Second pulse
  const body2 = noiseBurst({ duration: 0.7, attack: 0.005, freq: 180, Q: 0.6, vol: 0.35, delay: 0.12 });
  body2.filter.frequency.setValueAtTime(300, now() + 0.12);
  body2.filter.frequency.exponentialRampToValueAtTime(50, now() + 0.7);
  // Sub anchor
  osc({ freq: 45, freqEnd: 18, type: 'sine', duration: 0.5, vol: 0.1, attack: 0.02 });
  // Pressure wave
  const wave = noiseBurst({ duration: 0.6, attack: 0.01, freq: 1000, Q: 0.4, vol: 0.3 });
  wave.filter.frequency.setValueAtTime(2000, now());
  wave.filter.frequency.exponentialRampToValueAtTime(300, now() + 0.6);
  // Heavy debris
  for (let i = 0; i < 7; i++) {
    const d = 0.05 + Math.random() * 0.5;
    noiseBurst({ duration: 0.03 + Math.random() * 0.06, attack: 0.001,
                 freq: 600 + Math.random() * 3000, Q: 1 + Math.random() * 2,
                 vol: 0.05 + Math.random() * 0.08, delay: d, type: 'bandpass' });
  }
  // Crackle layer
  noiseBurst({ duration: 0.8, attack: 0.02, freq: 4000, Q: 1, vol: 0.1, type: 'highpass' });
  // Rumble tail
  const tail = noiseBurst({ duration: 1.5, attack: 0.1, freq: 250, Q: 0.4, vol: 0.12, delay: 0.15 });
  tail.filter.frequency.setValueAtTime(350, now() + 0.15);
  tail.filter.frequency.exponentialRampToValueAtTime(80, now() + 1.5);
  // Distant echo
  noiseBurst({ duration: 0.6, attack: 0.25, freq: 500, Q: 0.8, vol: 0.05, delay: 0.6 });
}

export function craterImpact(): void {
  if (!ensureContext()) return;
  duckMusicBus();
  // Initial crack
  noiseBurst({ duration: 0.04, attack: 0.001, freq: 1800, Q: 1.5, vol: 0.25 });
  // Body
  const body = noiseBurst({ duration: 0.5, attack: 0.003, freq: 120, Q: 0.8, vol: 0.4 });
  body.filter.frequency.setValueAtTime(180, now());
  body.filter.frequency.exponentialRampToValueAtTime(40, now() + 0.4);
  // Sub anchor
  osc({ freq: 45, freqEnd: 18, type: 'sine', duration: 0.3, vol: 0.1, attack: 0.01 });
  // Dirt spray
  const spray = noiseBurst({ duration: 0.35, attack: 0.01, freq: 800, Q: 0.8, vol: 0.2 });
  spray.filter.frequency.setValueAtTime(1200, now());
  spray.filter.frequency.exponentialRampToValueAtTime(300, now() + 0.35);
  // Ground crackle
  noiseBurst({ duration: 0.12, attack: 0.002, freq: 2500, Q: 2, vol: 0.12, type: 'bandpass' });
  // Settle rumble
  noiseBurst({ duration: 0.5, attack: 0.1, freq: 200, Q: 0.5, vol: 0.07, delay: 0.15 });
}

// ---------------------------------------------------------------------------
// SFX — Movement (ported from sfx_and_ambiance_v2.html)
// ---------------------------------------------------------------------------

export function footstepDefault(delay = 0, variation = 0): void {
  if (!ensureContext()) return;
  // Suited boot on hard ground. variation 0-1 shifts character for L/R alternation
  const vShift = variation * 200;
  const t = delay;
  // Primary contact — short filtered thud
  noiseBurst({ duration: 0.07, attack: 0.001, freq: 600 + vShift, Q: 1.5, vol: 0.4, delay: t });
  // Ground crunch — gritty texture
  noiseBurst({ duration: 0.09, attack: 0.002, freq: 2000 + vShift * 2, Q: 1.5, vol: 0.2, delay: t, type: 'bandpass' });
  // Suit creak — rustle on each step
  noiseBurst({ duration: 0.1, attack: 0.005, freq: 4000, Q: 2, vol: 0.08, delay: t + 0.015, type: 'bandpass' });
  // Sub thump — weight hitting ground
  noiseBurst({ duration: 0.1, attack: 0.002, freq: 100 + vShift * 0.3, Q: 0.8, vol: 0.25, delay: t });
  // Weight settle — tail
  noiseBurst({ duration: 0.08, attack: 0.01, freq: 800 + vShift, Q: 1, vol: 0.1, delay: t + 0.03 });
}

export function jumpLaunch(): void {
  if (!ensureContext()) return;
  // Crouch compression — suit/gear squeak
  noiseBurst({ duration: 0.1, attack: 0.003, freq: 1200, Q: 2, vol: 0.2, type: 'bandpass' });
  // Ground push-off — short impact
  noiseBurst({ duration: 0.07, attack: 0.001, freq: 500, Q: 1.5, vol: 0.35 });
  // Upward whoosh — filtered noise sweeping up
  const whoosh = noiseBurst({ duration: 0.45, attack: 0.02, freq: 1500, Q: 0.8, vol: 0.35, delay: 0.04 });
  whoosh.filter.frequency.setValueAtTime(400, now() + 0.04);
  whoosh.filter.frequency.exponentialRampToValueAtTime(2500, now() + 0.2);
  whoosh.filter.frequency.exponentialRampToValueAtTime(800, now() + 0.49);
  // Suit rustle — gear shifting on launch
  noiseBurst({ duration: 0.15, attack: 0.01, freq: 5000, Q: 1.5, vol: 0.1, delay: 0.02, type: 'bandpass' });
  // Thruster hint — faint hiss
  noiseBurst({ duration: 0.3, attack: 0.04, freq: 3500, Q: 0.5, vol: 0.12, delay: 0.06, type: 'highpass' });
}

export function jumpLand(): void {
  if (!ensureContext()) return;
  // Heavy contact — wider and messier than a footstep
  noiseBurst({ duration: 0.09, attack: 0.001, freq: 400, Q: 1, vol: 0.45 });
  noiseBurst({ duration: 0.06, attack: 0.001, freq: 1000, Q: 1.5, vol: 0.3 });
  // Sub impact
  const sub = noiseBurst({ duration: 0.2, attack: 0.002, freq: 100, Q: 0.8, vol: 0.4 });
  sub.filter.frequency.setValueAtTime(150, now());
  sub.filter.frequency.exponentialRampToValueAtTime(40, now() + 0.15);
  // Ground scatter — dirt/debris kicked up on landing
  for (let i = 0; i < 3; i++) {
    const d = 0.02 + Math.random() * 0.06;
    noiseBurst({ duration: 0.05 + Math.random() * 0.03, attack: 0.001,
                 freq: 1500 + Math.random() * 2000, Q: 2, vol: 0.1 + Math.random() * 0.08,
                 delay: d, type: 'bandpass' });
  }
  // Suit compression — gear absorbing impact
  noiseBurst({ duration: 0.12, attack: 0.005, freq: 3000, Q: 2, vol: 0.1, delay: 0.02, type: 'bandpass' });
  // Weight settle
  noiseBurst({ duration: 0.15, attack: 0.02, freq: 600, Q: 1, vol: 0.15, delay: 0.05 });
}

// ---------------------------------------------------------------------------
// Stinger priority — cancel previous stinger before playing new one
// ---------------------------------------------------------------------------

function beginStinger(): void {
  if (!stingerGain || !ctx) return;
  // Instantly silence any in-progress stinger
  const t = ctx.currentTime;
  stingerGain.gain.cancelScheduledValues(t);
  stingerGain.gain.setValueAtTime(0, t);
  // Restore gain for the new stinger (slight delay to let cancel take effect)
  stingerGain.gain.setValueAtTime(1.0, t + 0.01);
  // Route new stinger sounds through stingerGain → musicBus
  _activeBus = stingerGain;
}

function endStinger(): void {
  _activeBus = null;
}

// ---------------------------------------------------------------------------
// SFX — UI / Game State (ported from reference lines 597–639)
// ---------------------------------------------------------------------------

export function uiClick(): void {
  if (!ensureContext()) return;
  _activeBus = uiBus;
  osc({ freq: 1000, type: 'sine', duration: 0.06, vol: 0.12, attack: 0.002 });
  osc({ freq: 1500, type: 'sine', duration: 0.04, vol: 0.06, attack: 0.002, delay: 0.01 });
  _activeBus = null;
}

export function turnStart(): void {
  if (!ensureContext()) return;
  beginStinger();
  // Two-note alert — ascending
  osc({ freq: 600, type: 'sine', duration: 0.12, vol: 0.2, attack: 0.005 });
  osc({ freq: 800, type: 'sine', duration: 0.18, vol: 0.22, attack: 0.005, delay: 0.1 });
  // Soft shimmer
  noiseBurst({ duration: 0.15, attack: 0.02, freq: 4000, Q: 4, vol: 0.03, delay: 0.1 });
  endStinger();
}

export function turnEnd(): void {
  if (!ensureContext()) return;
  beginStinger();
  // Descending confirmation
  osc({ freq: 700, type: 'sine', duration: 0.1, vol: 0.15, attack: 0.005 });
  osc({ freq: 500, type: 'sine', duration: 0.15, vol: 0.12, attack: 0.005, delay: 0.08 });
  endStinger();
}

export function victory(): void {
  if (!ensureContext()) return;
  beginStinger();
  // Triumphant ascending arpeggio
  const notes = [523, 659, 784, 1047]; // C5 E5 G5 C6
  notes.forEach((f, i) => {
    osc({ freq: f, type: 'sine', duration: 0.2 + i * 0.05, vol: 0.2, attack: 0.005, delay: i * 0.12 });
    osc({ freq: f * 2, type: 'sine', duration: 0.15, vol: 0.05, attack: 0.005, delay: i * 0.12 }); // octave shimmer
  });
  // Final chord sustain
  osc({ freq: 523, type: 'sine', duration: 0.8, vol: 0.1, attack: 0.01, delay: 0.48 });
  osc({ freq: 659, type: 'sine', duration: 0.8, vol: 0.08, attack: 0.01, delay: 0.48 });
  osc({ freq: 784, type: 'sine', duration: 0.8, vol: 0.08, attack: 0.01, delay: 0.48 });
  osc({ freq: 1047, type: 'sine', duration: 0.8, vol: 0.1, attack: 0.01, delay: 0.48 });
  endStinger();
}

export function defeat(): void {
  if (!ensureContext()) return;
  beginStinger();
  // Descending minor — somber
  const notes = [440, 370, 311, 262]; // A4 F#4 Eb4 C4
  notes.forEach((f, i) => {
    osc({ freq: f, type: 'sine', duration: 0.25 + i * 0.08, vol: 0.18, attack: 0.01, delay: i * 0.18 });
  });
  // Low drone tail
  osc({ freq: 131, type: 'sine', duration: 1.2, vol: 0.1, attack: 0.1, delay: 0.6 });
  osc({ freq: 165, type: 'sine', duration: 1.0, vol: 0.06, attack: 0.1, delay: 0.6 }); // minor third
  endStinger();
}
